import { useState, useRef, useEffect, useMemo } from "react";
import { useSpatial } from "./SpatialContext";
import { confirmDialog } from "../lib/dialog";

const WORLD_DATA = [
  {
    id: "work",
    label: "Work Island",
    icon: "🏙️",
    shape: "M 60 160 C 50 100, 100 50, 180 44 C 260 38, 330 60, 360 100 C 390 140, 378 195, 348 228 C 318 262, 255 278, 180 268 C 105 258, 62 218, 60 160 Z",
    color: "#C8D8C0", shadowColor: "#A8B8A0", textureColor: "#B5C8AC",
    cx: 210, cy: 158,
    buildings: [
      {
        id: "neuroquest", label: "NeuroQuest",
        x: 130, y: 100, w: 88, h: 56, bColor: "#7BA08A",
        floors: [
          {
            id: "nq-f1", label: "Floor 1 — Active Build",
            rooms: [
              { id: "nq-f1-desk",       label: "Desk Room",       status: "current", hotspot: "desk"       },
              { id: "nq-f1-whiteboard", label: "Whiteboard",      status: "active",  hotspot: "whiteboard" },
              { id: "nq-f1-bookshelf",  label: "Bookshelf",       status: "recent",  hotspot: "bookshelf"  },
            ],
          },
          {
            id: "nq-f2", label: "Floor 2 — Research",
            rooms: [
              { id: "nq-f2-patent", label: "Patent Room",     status: "recent", hotspot: null },
              { id: "nq-f2-market", label: "Market Research", status: "empty",  hotspot: null },
            ],
          },
        ],
      },
      {
        id: "investor", label: "Investor Relations",
        x: 234, y: 160, w: 80, h: 48, bColor: "#8A9E8A",
        floors: [
          {
            id: "inv-f1", label: "Floor 1 — Outreach",
            rooms: [
              { id: "inv-f1-pitch",  label: "Pitch Deck",  status: "active", hotspot: null },
              { id: "inv-f1-crm",    label: "CRM Notes",   status: "recent", hotspot: null },
              { id: "inv-f1-follow", label: "Follow-ups",  status: "empty",  hotspot: null },
            ],
          },
        ],
      },
    ],
  },
  {
    id: "personal",
    label: "Personal Island",
    icon: "🌿",
    shape: "M 420 230 C 408 182, 445 138, 508 130 C 571 122, 622 152, 638 198 C 654 244, 628 296, 578 312 C 528 328, 474 308, 446 274 C 428 255, 420 248, 420 230 Z",
    color: "#D4C8B8", shadowColor: "#B4A898", textureColor: "#C8BAA5",
    cx: 530, cy: 222,
    buildings: [
      {
        id: "haven", label: "Haven",
        x: 480, y: 170, w: 96, h: 60, bColor: "#A89070",
        floors: [
          {
            id: "hav-f1", label: "Floor 1",
            rooms: [
              { id: "hav-couch", label: "Couch",    status: "recent", hotspot: "couch"    },
              { id: "hav-cal",   label: "Calendar", status: "active", hotspot: "calendar" },
            ],
          },
        ],
      },
    ],
  },
  {
    id: "projects",
    label: "Side Projects",
    icon: "🚀",
    shape: "M 100 370 C 92 338, 118 308, 158 300 C 198 292, 238 308, 252 338 C 266 368, 250 404, 218 416 C 186 428, 148 416, 124 394 C 108 380, 100 382, 100 370 Z",
    color: "#C8C0D8", shadowColor: "#A8A0B8", textureColor: "#BCB4CC",
    cx: 176, cy: 360,
    buildings: [
      {
        id: "allrise", label: "AllRise",
        x: 132, y: 328, w: 76, h: 48, bColor: "#8878A0",
        floors: [
          {
            id: "ar-f1", label: "Floor 1 — Concept",
            rooms: [
              { id: "ar-brief", label: "Brief Room", status: "empty", hotspot: null },
            ],
          },
        ],
      },
    ],
  },
];

// ── Island layout configs — assigned by index when Brain Dump builds the world ──
const ISLAND_CONFIGS = [
  { icon: "🏙️", color: "#C8D8C0", shadowColor: "#A8B8A0", textureColor: "#B5C8AC", shape: "M 60 160 C 50 100, 100 50, 180 44 C 260 38, 330 60, 360 100 C 390 140, 378 195, 348 228 C 318 262, 255 278, 180 268 C 105 258, 62 218, 60 160 Z", cx: 210, cy: 158, bColor: "#7BA08A" },
  { icon: "🌿", color: "#D4C8B8", shadowColor: "#B4A898", textureColor: "#C8BAA5", shape: "M 420 230 C 408 182, 445 138, 508 130 C 571 122, 622 152, 638 198 C 654 244, 628 296, 578 312 C 528 328, 474 308, 446 274 C 428 255, 420 248, 420 230 Z", cx: 530, cy: 222, bColor: "#A89070" },
  { icon: "🚀", color: "#C8C0D8", shadowColor: "#A8A0B8", textureColor: "#BCB4CC", shape: "M 100 370 C 92 338, 118 308, 158 300 C 198 292, 238 308, 252 338 C 266 368, 250 404, 218 416 C 186 428, 148 416, 124 394 C 108 380, 100 382, 100 370 Z", cx: 176, cy: 360, bColor: "#8878A0" },
  { icon: "🌊", color: "#B8CCD8", shadowColor: "#98ACB8", textureColor: "#ACC0CC", shape: "M 440 360 C 432 328, 458 300, 498 294 C 538 288, 576 308, 588 340 C 600 372, 582 408, 550 418 C 518 428, 480 414, 458 390 C 443 374, 440 374, 440 360 Z", cx: 516, cy: 358, bColor: "#6890A0" },
];

// Natural bounding-box centers + sizes for each ISLAND_CONFIGS shape
const WG_SHAPE_META = [
  { cx: 210, cy: 158, w: 340, h: 240 },
  { cx: 530, cy: 222, w: 246, h: 206 },
  { cx: 176, cy: 360, w: 174, h: 136 },
  { cx: 516, cy: 358, w: 168, h: 140 },
];

// Building position grid within an island — offset from island center
const BUILDING_OFFSETS = [
  { dx: -44, dy: -28 }, { dx: 20, dy: -20 }, { dx: -30, dy: 20 }, { dx: 24, dy: 22 },
];

function seededJitter(id, axis) {
  let h = 0;
  const s = id + axis;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return ((h >>> 0) / 0xFFFFFFFF) * 2 - 1;
}

// ── Convert SpatialContext world → WorldGuide display format ──
function normalizeWorld(world) {
  if (!world?.districts?.length) return null;

  const allIslands = [];
  for (const district of world.districts) {
    for (const island of (district.islands || [])) {
      allIslands.push({ district, island });
    }
  }
  if (!allIslands.length) return null;

  const n = allIslands.length;
  const cols = Math.ceil(Math.sqrt(n));
  const rows = Math.ceil(n / (cols || 1));
  const CANVAS_W = 600;
  const CANVAS_H = 500;
  const MARGIN = 50;
  const cellW = (CANVAS_W - MARGIN * 2) / (cols || 1);
  const cellH = (CANVAS_H - MARGIN * 2) / (rows || 1);

  const islands = allIslands.map(({ district, island }, iIdx) => {
    const cfg  = ISLAND_CONFIGS[iIdx % ISLAND_CONFIGS.length];
    const meta = WG_SHAPE_META[iIdx % WG_SHAPE_META.length];
    const gridCol = iIdx % cols;
    const gridRow = Math.floor(iIdx / cols);
    const jitterX = seededJitter(island.id, "x") * cellW * 0.15;
    const jitterY = seededJitter(island.id, "y") * cellH * 0.15;
    const cx = MARGIN + gridCol * cellW + cellW / 2 + jitterX;
    const cy = MARGIN + gridRow * cellH + cellH / 2 + jitterY;
    const scale = (Math.min(cellW, cellH) * 0.92) / Math.max(meta.w, meta.h);
    const groupTransform = `translate(${cx},${cy}) scale(${scale.toFixed(4)}) translate(${-meta.cx},${-meta.cy})`;

    const buildings = (island.buildings || []).map((building, bIdx) => {
      const off = BUILDING_OFFSETS[bIdx % BUILDING_OFFSETS.length];
      return {
        id: building.id,
        label: building.name,
        x: meta.cx + off.dx - 40,
        y: meta.cy + off.dy - 24,
        w: 80, h: 48,
        bColor: cfg.bColor,
        floors: (building.floors || []).map(floor => ({
          id: floor.id,
          label: floor.name,
          rooms: (floor.rooms || []).map(room => ({
            id: room.id,
            label: room.name,
            status: "active",
            hotspot: null,
          })),
        })),
      };
    });

    return {
      id: island.id,
      label: island.name,
      icon: cfg.icon,
      shape: cfg.shape,
      color: cfg.color,
      shadowColor: cfg.shadowColor,
      textureColor: cfg.textureColor,
      cx: meta.cx,
      cy: meta.cy,
      groupTransform,
      buildings,
    };
  });

  return islands.length ? islands : null;
}


const STATUS = {
  current: { color: "#4DB8B5", label: "Here now" },
  active:  { color: "#22C55E", label: "Active"   },
  recent:  { color: "#F59E0B", label: "Recent"   },
  empty:   { color: "#CBD5E1", label: "Empty"    },
};

function findBreadcrumb(roomId, worldData) {
  for (const island of worldData) {
    for (const building of island.buildings) {
      for (const floor of (building.floors || [])) {
        for (const room of (floor.rooms || [])) {
          if (room.id === roomId) return [island.label, building.label, floor.label, room.label];
        }
      }
    }
  }
  return [];
}

function findBuildingId(roomId, worldData) {
  for (const island of worldData) {
    for (const building of island.buildings) {
      for (const floor of (building.floors || [])) {
        for (const room of (floor.rooms || [])) {
          if (room.id === roomId) return building.id;
        }
      }
    }
  }
  return null;
}

function getFirstRoomForBuilding(buildingId, worldData) {
  for (const island of worldData) {
    for (const building of island.buildings) {
      if (building.id === buildingId) {
        const room = building.floors?.[0]?.rooms?.[0];
        if (room) return { ...room };
      }
    }
  }
  return null;
}

// ── Haven spaces ────────────────────────────────────────────────
const HAVEN_SPACES = [
  { id: "lighthouse", icon: "🔆", label: "The Lighthouse",  sub: "Mission control. World overview. Settings.",    status: "available",    hotspot: "lighthouse" },
  { id: "observatory", icon: "🔭", label: "The Observatory", sub: "Your behavioral patterns. Journaling. Reports.", status: "coming_soon",  hotspot: null },
  { id: "grove",       icon: "🌿", label: "The Grove",       sub: "Breathe. Meditate. Reset.",                    status: "available",    hotspot: "grove"      },
  { id: "cafe",        icon: "☕", label: "The Cafe",         sub: "Body doubling. Low-pressure social.",          status: "coming_soon",  hotspot: null },
  { id: "rec_room",    icon: "🎮", label: "The Rec Room",    sub: "Games. Watch together. Just play.",            status: "coming_soon",  hotspot: null },
  { id: "workshop",    icon: "🔨", label: "The Workshop",    sub: "Creator marketplace. Templates. Economy.",     status: "coming_soon",  hotspot: null },
  { id: "library",     icon: "📚", label: "The Library",     sub: "Courses. Resources. Skill-building.",          status: "coming_soon",  hotspot: null },
  { id: "lab",         icon: "🧪", label: "The Lab",         sub: "Learn by doing with Lumen.",                   status: "coming_soon",  hotspot: null },
  { id: "gray_havens", icon: "🌅", label: "Gray Havens",     sub: "Completed. Sunsetted. Archived.",              status: "available",    hotspot: "gray_havens" },
];

function HavenSection({ onNavigate }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-[4px]">
      {/* Header */}
      <div onClick={() => setOpen(o => !o)}
        className="flex items-center gap-[6px] p-[7px_10px] cursor-pointer rounded-lg border mb-[6px] transition-all duration-150"
        style={{
          background: "linear-gradient(135deg, rgba(168,144,112,0.12) 0%, rgba(77,184,181,0.08) 100%)",
          borderColor: "rgba(168,144,112,0.2)",
          marginBottom: open ? 6 : 0
        }}
        onMouseEnter={e => e.currentTarget.style.background = "linear-gradient(135deg, rgba(168,144,112,0.18) 0%, rgba(77,184,181,0.12) 100%)"}
        onMouseLeave={e => e.currentTarget.style.background = "linear-gradient(135deg, rgba(168,144,112,0.12) 0%, rgba(77,184,181,0.08) 100%)"}>
        <span className="text-[13px]">🏝</span>
        <div className="flex-1">
          <div className="text-[11px] font-bold" style={{ color: "#A89070" }}>Haven</div>
          <div className="text-[9px] text-slate-400">Your personal island</div>
        </div>
        <span className="text-[8px] text-slate-400 inline-block transition-transform duration-150" style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
      </div>

      {/* Spaces list */}
      {open && (
        <div className="flex flex-col gap-[2px]">
          {HAVEN_SPACES.map(space => {
            const available = space.status === "available";
            return (
              <div key={space.id}
                onClick={() => onNavigate({ id: space.id, hotspot: space.hotspot, isHaven: true })}
                className="flex items-center gap-2 py-[6px] px-[10px_10px_6px_14px] rounded-[7px] transition-all duration-150"
                style={{
                  cursor: "pointer",
                  opacity: available ? 1 : 0.75
                }}
                onMouseEnter={e => { if (available) e.currentTarget.style.background = "rgba(168,144,112,0.08)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                <span className="text-[14px] flex-shrink-0">{space.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-semibold" style={{ color: available ? "#1E293B" : "#94A3B8" }}>{space.label}</div>
                  <div className="text-[9px] text-slate-400 mt-[1px] whitespace-nowrap overflow-hidden text-ellipsis">{space.sub}</div>
                </div>
                {available
                  ? <span className="text-[8px]" style={{ color: "#4DB8B5" }}>→</span>
                  : <span className="text-[8px] italic" style={{ color: "#CBD5E1" }}>soon</span>
                }
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const WATER_LINES = Array.from({ length: 14 }, (_, i) => ({
  y: 20 + i * 36, x1: 10 + (i % 3) * 12, x2: 590 + (i % 2) * 15,
  opacity: 0.05 + (i % 4) * 0.018,
}));

function MapSVG({ width, height, scale, offsetX, offsetY, currentRoom, hoveredBuilding, onBuildingClick, onBuildingHover, worldData }) {
  const currentBuildingId = findBuildingId(currentRoom, worldData);
  return (
    <svg width={width} height={height}
      viewBox={`${-offsetX / scale} ${-offsetY / scale} ${width / scale} ${height / scale}`}
      className="block"
    >
      <defs>
        <radialGradient id="wg_water" cx="50%" cy="50%" r="70%">
          <stop offset="0%"   stopColor="#4A7A9B" />
          <stop offset="100%" stopColor="#2C5272" />
        </radialGradient>
        <filter id="wg_isle"><feDropShadow dx="2" dy="3" stdDeviation="5" floodColor="rgba(0,0,0,0.22)" /></filter>
        <filter id="wg_bldg"><feDropShadow dx="1" dy="2" stdDeviation="2" floodColor="rgba(0,0,0,0.28)" /></filter>
      </defs>
      <rect x="-100" y="-100" width="900" height="700" fill="url(#wg_water)" />
      {WATER_LINES.map((l, i) => (
        <line key={i} x1={l.x1} y1={l.y} x2={l.x2} y2={l.y + 6} stroke="white" strokeWidth="1.2" opacity={l.opacity} strokeLinecap="round" />
      ))}
      <g transform="translate(562,452)" opacity="0.3">
        <circle r="14" fill="none" stroke="white" strokeWidth="0.7" />
        <text textAnchor="middle" y="-17" fill="white" fontSize="7" fontWeight="700" fontFamily="Georgia,serif">N</text>
        <line x1="0" y1="-11" x2="0" y2="11" stroke="white" strokeWidth="0.8" />
        <line x1="-11" y1="0" x2="11" y2="0" stroke="white" strokeWidth="0.8" />
        <polygon points="0,-10 2,-3 0,-6 -2,-3" fill="white" />
      </g>
      {worldData.map(island => (
        <g key={island.id} transform={island.groupTransform}>
          <path d={island.shape} fill={island.shadowColor} transform="translate(3,4)" opacity="0.45" />
          <path d={island.shape} fill={island.color} filter="url(#wg_isle)" stroke="rgba(255,255,255,0.25)" strokeWidth="1" />
          {Array.from({ length: 10 }, (_, i) => {
            const angle = (i / 10) * Math.PI * 2;
            const r = 20 + (i % 3) * 12;
            return <circle key={i} cx={island.cx + Math.cos(angle) * r} cy={island.cy + Math.sin(angle) * r * 0.55} r={1.2 + (i % 2)} fill={island.textureColor} opacity="0.55" />;
          })}
          <text x={island.cx} y={island.cy + 60} textAnchor="middle" fill="rgba(20,30,20,0.65)" fontSize="8" fontWeight="700" fontFamily="Georgia,serif" letterSpacing="0.4">
            {island.label.toUpperCase()}
          </text>
          {island.buildings.map(b => {
            const isCurrent = b.id === currentBuildingId;
            const isHovered = hoveredBuilding === b.id;
            return (
              <g key={b.id} onClick={() => onBuildingClick(b)} onMouseEnter={() => onBuildingHover(b.id)} onMouseLeave={() => onBuildingHover(null)} style={{ cursor: "pointer" }}>
                <rect x={b.x + 2} y={b.y + 2} width={b.w} height={b.h} rx="3" fill="rgba(0,0,0,0.18)" />
                <rect x={b.x} y={b.y} width={b.w} height={b.h} rx="3"
                  fill={isHovered ? "#4DB8B5" : isCurrent ? "#5CC8C5" : b.bColor}
                  stroke={isCurrent || isHovered ? "#4DB8B5" : "rgba(255,255,255,0.35)"}
                  strokeWidth={isCurrent ? "2" : "1"} filter="url(#wg_bldg)"
                  style={{ transition: "fill 0.13s" }} />
                {Array.from({ length: Math.floor(b.w / 16) }, (_, i) => (
                  <rect key={i} x={b.x + 6 + i * 16} y={b.y + 10} width="7" height="5" rx="1" fill="rgba(255,255,255,0.22)" />
                ))}
                <text x={b.x + b.w / 2} y={b.y + b.h / 2 + 3} textAnchor="middle" fill="white" fontSize="7" fontWeight="700" fontFamily="'DM Sans',sans-serif" style={{ pointerEvents: "none" }}>
                  {b.label.length > 11 ? b.label.slice(0, 10) + "…" : b.label}
                </text>
              </g>
            );
          })}
          {(() => {
            const building = island.buildings.find(b => b.id === currentBuildingId);
            if (!building) return null;
            const px = building.x + building.w / 2;
            const py = building.y + building.h / 2;
            return (
              <g>
                <circle cx={px} cy={py} r="16" fill="#4DB8B5" opacity="0.2">
                  <animate attributeName="r"       values="12;20;12"       dur="2.4s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.25;0.05;0.25" dur="2.4s" repeatCount="indefinite" />
                </circle>
                <circle cx={px} cy={py} r="4" fill="#4DB8B5" stroke="white" strokeWidth="1.5" />
              </g>
            );
          })()}
        </g>
      ))}
    </svg>
  );
}

function FullMapOverlay({ currentRoom, onClose, onNavigate, worldData }) {
  const [hoveredBuilding, setHoveredBuilding] = useState(null);
  const [tooltip, setTooltip]                = useState(null);
  const [offset, setOffset]                  = useState({ x: 0, y: 0 });
  const [zoom, setZoom]                      = useState(1);
  const [dragging, setDragging]              = useState(false);
  const [mounted, setMounted]                = useState(false);
  const [closing, setClosing]                = useState(false);
  const dragStart = useRef(null);

  // Mount animation
  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
  }, []);

  // ESC to close
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 380);
  };

  const onMD = (e) => { setDragging(true); dragStart.current = { x: e.clientX - offset.x, y: e.clientY - offset.y }; };
  const onMM = (e) => { if (!dragging) return; setOffset({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y }); };
  const onMU = () => setDragging(false);
  const onW  = (e) => { e.preventDefault(); setZoom(z => Math.max(0.4, Math.min(4, z * (e.deltaY > 0 ? 0.9 : 1.1)))); };

  const handleClick = (b) => { const r = getFirstRoomForBuilding(b.id, worldData); if (r) { onNavigate(r); handleClose(); } };
  const handleHover = (id) => {
    setHoveredBuilding(id);
    if (id) { const isl = worldData.find(i => i.buildings.some(b => b.id === id)); setTooltip(isl?.buildings.find(b => b.id === id)?.label || null); }
    else setTooltip(null);
  };

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const active = mounted && !closing;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center transition-all duration-[380ms] ease-in-out"
      style={{
        background: closing ? "rgba(0,0,0,0)" : mounted ? "rgba(0,0,0,0.92)" : "rgba(0,0,0,0)",
      }}>

      {/* Map frame - the Skyrim-style reveal */}
      <div className="absolute inset-0 transition-all"
        style={{
          opacity: active ? 1 : 0,
          transform: active ? "scale(1)" : "scale(1.06)",
          transitionDuration: closing ? "0.32s, 0.38s" : "0.42s, 0.48s",
          transitionProperty: "opacity, transform",
          transitionTimingFunction: closing
            ? "ease, cubic-bezier(0.4,0,1,1)"
            : "ease, cubic-bezier(0,0,0.2,1)",
          transformOrigin: "center center",
        }}>

        {/* Deep vignette */}
        <div className="absolute inset-0 z-[2] pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at center, transparent 38%, rgba(0,0,0,0.55) 72%, rgba(0,0,0,0.88) 100%)",
          }} />

        {/* Scanline texture overlay for parchment feel */}
        <div className="absolute inset-0 z-[2] pointer-events-none opacity-[0.04]"
          style={{
            backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,1) 2px, rgba(255,255,255,1) 3px)",
          }} />

        {/* The map canvas */}
        <div onMouseDown={onMD} onMouseMove={onMM} onMouseUp={onMU} onMouseLeave={onMU} onWheel={onW}
          className="w-full h-full overflow-hidden"
          style={{ cursor: dragging ? "grabbing" : "crosshair" }}>
          <MapSVG width={vw} height={vh} scale={1 / zoom}
            offsetX={offset.x + (vw - 600) / 2} offsetY={offset.y + (vh - 500) / 2}
            currentRoom={currentRoom} hoveredBuilding={hoveredBuilding}
            onBuildingClick={handleClick} onBuildingHover={handleHover} worldData={worldData} />
        </div>

        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 z-10 p-[18px_24px] flex items-center justify-between transition-all duration-[400ms] ease-in-out delay-150"
          style={{
            background: "linear-gradient(to bottom, rgba(0,0,0,0.75) 0%, transparent 100%)",
            opacity: active ? 1 : 0,
            transform: active ? "translateY(0)" : "translateY(-12px)",
          }}>
          <div>
            <div className="font-['Georgia',serif] text-[20px] font-bold text-white tracking-[0.12em] uppercase">
              Your World
            </div>
            <div className="text-[10px] tracking-[0.08em] mt-[2px] font-['DM_Sans']" style={{ color: "rgba(255,255,255,0.35)" }}>
              scroll to zoom · drag to pan · click a building to enter
            </div>
          </div>
          <button onClick={handleClose}
            className="rounded-lg py-[7px] px-4 text-[11px] font-semibold font-['DM_Sans'] tracking-[0.04em] cursor-pointer transition-all duration-150"
            style={{
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.18)",
              color: "rgba(255,255,255,0.7)"
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; e.currentTarget.style.color = "white"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}>
            ESC — Close
          </button>
        </div>

        {/* Zoom controls */}
        <div className="absolute right-5 top-1/2 -translate-y-1/2 z-10 flex flex-col gap-[5px] transition-opacity duration-[400ms] delay-[250ms]"
          style={{ opacity: active ? 1 : 0 }}>
          {[{ lbl: "+", fn: () => setZoom(z => Math.min(4, z * 1.25)) },
            { lbl: "−", fn: () => setZoom(z => Math.max(0.4, z * 0.8)) },
            { lbl: "⌖", fn: () => { setZoom(1); setOffset({ x: 0, y: 0 }); } }].map(b => (
            <button key={b.lbl} onClick={b.fn}
              className="w-[34px] h-[34px] rounded-lg flex items-center justify-center cursor-pointer transition-all duration-150 font-['DM_Sans']"
              style={{
                background: "rgba(0,0,0,0.5)",
                border: "1px solid rgba(255,255,255,0.15)",
                color: "rgba(255,255,255,0.7)",
                fontSize: b.lbl === "⌖" ? 14 : 18
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(77,184,181,0.25)"; e.currentTarget.style.borderColor = "#4DB8B5"; e.currentTarget.style.color = "#4DB8B5"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(0,0,0,0.5)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}>
              {b.lbl}
            </button>
          ))}
        </div>

        {/* Building tooltip */}
        {tooltip && (
          <div className="absolute bottom-14 left-1/2 -translate-x-1/2 rounded-[10px] py-[9px] px-5 text-white text-center pointer-events-none backdrop-blur-lg"
            style={{
              background: "rgba(0,0,0,0.85)",
              border: "1px solid rgba(77,184,181,0.35)",
            }}>
            <div className="text-[14px] font-bold font-['DM_Sans'] tracking-[0.02em]">{tooltip}</div>
            <div className="text-[9px] mt-[3px] font-['DM_Sans'] tracking-[0.06em] uppercase" style={{ color: "#4DB8B5" }}>click to enter</div>
          </div>
        )}

        {/* Legend */}
        <div className="absolute bottom-5 left-5 z-10 rounded-[10px] py-[9px] px-[14px] backdrop-blur-lg transition-all duration-[400ms] delay-[300ms]"
          style={{
            background: "rgba(0,0,0,0.6)",
            border: "1px solid rgba(255,255,255,0.08)",
            opacity: active ? 1 : 0,
            transform: active ? "translateY(0)" : "translateY(10px)",
          }}>
          <div className="text-[7px] font-bold uppercase tracking-[0.1em] mb-[6px] font-['DM_Sans']" style={{ color: "rgba(255,255,255,0.3)" }}>Legend</div>
          {[
            { color: "#4DB8B5", label: "You are here" },
            { color: "#7BA08A", label: "Building" },
            { color: "#4A7A9B", label: "Open water" },
          ].map(item => (
            <div key={item.label} className="flex items-center gap-[7px] mb-[3px]">
              <div className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: item.color }} />
              <span className="text-[9px] font-['DM_Sans']" style={{ color: "rgba(255,255,255,0.5)" }}>{item.label}</span>
            </div>
          ))}
        </div>

      </div>

      <style>{`
        @keyframes mapReveal {
          from { opacity: 0; transform: scale(1.08); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

function RoomRow({ room, currentRoom, onNavigate }) {
  const isCurrent = room.id === currentRoom;
  const s = STATUS[room.status] || STATUS.empty;
  return (
    <div onClick={() => onNavigate(room)}
      className="flex items-center gap-[6px] py-[5px] px-[8px_8px_5px_12px] rounded-md cursor-pointer mb-[1px] border transition-all duration-150"
      style={{
        background: isCurrent ? "rgba(77,184,181,0.10)" : "transparent",
        borderColor: isCurrent ? "rgba(77,184,181,0.25)" : "transparent"
      }}
      onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.background = "rgba(0,0,0,0.03)"; }}
      onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.background = "transparent"; }}
    >
      <span className="text-[8px] flex-shrink-0" style={{ color: s.color }}>●</span>
      <span className="text-[11px] flex-1 leading-[1.3]" style={{
        color: isCurrent ? "#0F172A" : "#334155",
        fontWeight: isCurrent ? 600 : 400
      }}>{room.label}</span>
      {isCurrent && <span className="text-[8px] font-bold" style={{ color: "#4DB8B5" }}>YOU</span>}
      {!isCurrent && room.hotspot && <span className="text-[8px] text-slate-300">→</span>}
    </div>
  );
}

function FloorSection({ floor, currentRoom, onNavigate, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const hasCurrentRoom = (floor.rooms || []).some(r => r.id === currentRoom);
  return (
    <div className="mb-[2px]">
      <div onClick={() => setOpen(o => !o)}
        className="flex items-center gap-[5px] p-1 px-2 cursor-pointer rounded-[5px] hover:bg-black/[0.03]">
        <span className="text-[8px] text-slate-400 inline-block transition-transform duration-150" style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
        <span className="text-[10px] font-semibold text-slate-500 flex-1">{floor.label}</span>
        {hasCurrentRoom && <span className="w-[5px] h-[5px] rounded-full flex-shrink-0" style={{ background: "#4DB8B5" }} />}
      </div>
      {open && <div className="pl-1">{(floor.rooms || []).map(room => <RoomRow key={room.id} room={room} currentRoom={currentRoom} onNavigate={onNavigate} />)}</div>}
    </div>
  );
}

function BuildingSection({ building, currentRoom, onNavigate, onDelete }) {
  const [open, setOpen] = useState(false);
  const hasCurrentRoom = (building.floors || []).some(f => (f.rooms || []).some(r => r.id === currentRoom));
  const totalRooms  = (building.floors || []).reduce((n, f) => n + (f.rooms || []).length, 0);
  const activeRooms = (building.floors || []).reduce((n, f) => n + (f.rooms || []).filter(r => r.status !== "empty").length, 0);
  return (
    <div className="mb-1 bg-white/60 rounded-lg overflow-hidden border border-slate-100">
      <div onClick={() => setOpen(o => !o)}
        className="flex items-center gap-[7px] p-2 px-[10px] cursor-pointer transition-all"
        style={{ background: open ? "rgba(255,255,255,0.9)" : "transparent" }}
        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.9)"}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = "transparent"; }}
      >
        <div className="flex-1">
          <div className="text-[11px] font-bold text-slate-800 flex items-center gap-1">
            {building.label}
            {hasCurrentRoom && <span className="w-[5px] h-[5px] rounded-full" style={{ background: "#4DB8B5" }} />}
          </div>
          <div className="text-[8px] text-slate-400">{activeRooms}/{totalRooms} rooms active</div>
        </div>
        <button onClick={e => { e.stopPropagation(); onDelete && onDelete(building.id); }}
          className="bg-transparent border-none text-red-500 text-[11px] cursor-pointer py-0 px-1 rounded leading-none"
          title="Delete building">×</button>
        <span className="text-[8px] text-slate-300 inline-block transition-transform duration-150" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
      </div>
      {open && (
        <div className="p-[4px_6px_6px]">
          {(building.floors || []).map(floor => (
            <FloorSection key={floor.id} floor={floor} currentRoom={currentRoom} onNavigate={onNavigate}
              defaultOpen={(floor.rooms || []).some(r => r.id === currentRoom) || (building.floors || []).length === 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function IslandSection({ island, currentRoom, onNavigate, onDelete }) {
  const [open, setOpen] = useState(false);
  const hasCurrentRoom = island.buildings.some(b => (b.floors || []).some(f => (f.rooms || []).some(r => r.id === currentRoom)));
  return (
    <div className="mb-[2px]">
      <div onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 p-2 cursor-pointer rounded-md hover:bg-black/[0.02]">
        <span className="text-base">{island.icon}</span>
        <span className="text-[12px] font-bold text-slate-900 flex-1">{island.label}</span>
        {hasCurrentRoom && <span className="text-[8px] font-bold" style={{ color: "#4DB8B5" }}>●</span>}
        <button onClick={e => { e.stopPropagation(); onDelete && onDelete(island.id); }}
          className="bg-transparent border-none text-red-500 text-[11px] cursor-pointer py-0 px-1 rounded leading-none"
          title="Delete island">×</button>
        <span className="text-[9px] text-slate-300 inline-block transition-transform duration-150" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
      </div>
      {open && (
        <div className="pl-1 pb-1">
          {island.buildings.map(b => <BuildingSection key={b.id} building={b} currentRoom={currentRoom} onNavigate={onNavigate} onDelete={onDelete} />)}
        </div>
      )}
    </div>
  );
}

export default function WorldGuide({ isOpen, onClose, currentRoom = "nq-f1-desk", onNavigate, onBrainDump }) {
  const { world, setWorld, deleteNode, saveWorld } = useSpatial();
  const activeWorldData = useMemo(() => normalizeWorld(world) || [], [world]);

  const handleDelete = (id) => {
    if (!deleteNode) return;
    deleteNode(id);
  };

  const handleResetWorld = async () => {
    const confirmed = await confirmDialog({
      title: "Reset your world?",
      message: "Reset your entire world? This cannot be undone.",
      confirmLabel: "Reset",
      danger: true,
    });
    if (!confirmed) return;
    const empty = { id: 'world-1', name: 'My World', districts: [] };
    setWorld(empty);
    if (saveWorld) saveWorld(empty);
  };

  const [search, setSearch]               = useState("");
  const [mapExpanded, setMapExpanded]     = useState(false);
  const [fullMap, setFullMap]             = useState(false);
  const [hoveredBuilding, setHoveredBuilding] = useState(null);
  const [tooltip, setTooltip]             = useState(null);
  const [mapOffset, setMapOffset]         = useState({ x: 0, y: 0 });
  const [mapDragging, setMapDragging]     = useState(false);
  const mapDragStart = useRef(null);

  const breadcrumb = findBreadcrumb(currentRoom, activeWorldData);

  const allRooms = activeWorldData.flatMap(island =>
    island.buildings.flatMap(building =>
      (building.floors || []).flatMap(floor =>
        (floor.rooms || []).map(room => ({ ...room, path: `${island.label} › ${building.label} › ${floor.label}` }))
      )
    )
  );

  const filtered = search.trim()
    ? allRooms.filter(r => r.label.toLowerCase().includes(search.toLowerCase()) || r.path.toLowerCase().includes(search.toLowerCase()))
    : null;

  const handleNavigate = (room) => {
    if (room.isHaven) {
      if (onNavigate) onNavigate(room);
      return;
    }
    if (onNavigate) onNavigate(room);
    if (window.onHotspotClick && room.hotspot) window.onHotspotClick(room.hotspot);
  };

  const onMapMD = (e) => { setMapDragging(true); mapDragStart.current = { x: e.clientX - mapOffset.x, y: e.clientY - mapOffset.y }; };
  const onMapMM = (e) => { if (!mapDragging) return; setMapOffset({ x: e.clientX - mapDragStart.current.x, y: e.clientY - mapDragStart.current.y }); };
  const onMapMU = () => setMapDragging(false);

  const handleBuildingClick = (b) => { const r = getFirstRoomForBuilding(b.id, activeWorldData); if (r) handleNavigate(r); };
  const handleBuildingHover = (id) => {
    setHoveredBuilding(id);
    if (id) { const isl = activeWorldData.find(i => i.buildings.some(b => b.id === id)); setTooltip(isl?.buildings.find(b => b.id === id)?.label || null); }
    else setTooltip(null);
  };

  const MAP_H = mapExpanded ? 280 : 160;
  const MAP_W = 258;

  if (!isOpen) return null;

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-40 bg-black/[0.15]" />

      {/* outer panel - flex column, overflow hidden */}
      <div onClick={e => e.stopPropagation()} className="fixed left-[46px] top-[50px] bottom-5 w-[280px] z-[41] bg-slate-50/98 rounded-r-xl shadow-[4px_0_24px_rgba(0,0,0,0.10)] flex flex-col overflow-hidden font-['DM_Sans']">

        {/* sticky header */}
        <div className="flex-shrink-0 p-[14px_14px_10px] border-b border-slate-200 bg-white sticky top-0 z-[5]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[13px] font-bold text-slate-900">World Guide</span>
            <button onClick={onClose} className="bg-transparent border-none text-[14px] cursor-pointer text-slate-300 p-[2px]">✕</button>
          </div>
          {breadcrumb.length > 0 && (
            <div className="rounded-[7px] p-[6px_8px] mb-2 border" style={{
              background: "rgba(77,184,181,0.08)",
              borderColor: "rgba(77,184,181,0.2)"
            }}>
              <div className="text-[8px] font-bold uppercase tracking-[0.06em] mb-[3px]" style={{ color: "#4DB8B5" }}>You are here</div>
              <div className="flex flex-wrap gap-[2px] items-center">
                {breadcrumb.map((crumb, i) => (
                  <span key={i} className="flex items-center gap-[2px]">
                    <span className="text-[10px]" style={{
                      color: i === breadcrumb.length - 1 ? "#0F172A" : "#64748B",
                      fontWeight: i === breadcrumb.length - 1 ? 600 : 400
                    }}>{crumb}</span>
                    {i < breadcrumb.length - 1 && <span className="text-[8px] text-slate-300">›</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="relative">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search rooms..."
              className="w-full p-[6px_28px_6px_10px] rounded-[7px] border border-slate-200 bg-slate-50 text-[11px] outline-none text-slate-700 box-border" />
            {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 bg-transparent border-none text-[11px] cursor-pointer text-slate-300">✕</button>}
          </div>
        </div>

        {/* legend - sticky below header */}
        <div className="flex-shrink-0 flex gap-[10px] p-[6px_14px] border-b border-slate-100 bg-white sticky top-0 z-[4]">
          {Object.entries(STATUS).map(([key, val]) => (
            <div key={key} className="flex items-center gap-[3px]">
              <span className="text-[8px]" style={{ color: val.color }}>●</span>
              <span className="text-[8px] text-slate-400">{val.label}</span>
            </div>
          ))}
        </div>

        {/* scrollable middle - list + map scroll together */}
        <div className="flex-1 overflow-y-auto">
          {/* list */}
          <div className="p-2 px-[10px]">
            {filtered ? (
              <div>
                <div className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.06em] mb-[6px]">
                  {filtered.length} result{filtered.length !== 1 ? "s" : ""}
                </div>
                {filtered.length === 0 && <div className="text-[11px] text-slate-400 text-center py-5">No rooms found.</div>}
                {filtered.map(room => (
                  <div key={room.id}>
                    <div className="text-[8px] text-slate-400 mb-[1px] pl-2">{room.path}</div>
                    <RoomRow room={room} currentRoom={currentRoom} onNavigate={handleNavigate} />
                  </div>
                ))}
              </div>
            ) : (
              <>
                {activeWorldData.map(island => <IslandSection key={island.id} island={island} currentRoom={currentRoom} onNavigate={handleNavigate} onDelete={handleDelete} />)}
                {activeWorldData.length > 0 && <div className="h-[1px] bg-slate-100 my-2" />}
                <HavenSection onNavigate={handleNavigate} />
              </>
            )}
          </div>

        </div>

        {/* footer - always pinned at bottom, content scrolls behind it */}
        <div className="flex-shrink-0 p-2 px-3 pb-3 bg-white border-t border-slate-100 flex flex-col gap-[6px]">
          <button onClick={() => { setFullMap(true); }}
            className="w-full p-2 px-3 rounded-lg border-none text-[11px] font-bold cursor-pointer transition-all duration-150"
            style={{ background: "rgba(77,184,181,0.12)", color: "#4DB8B5" }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(77,184,181,0.2)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(77,184,181,0.12)"}>
            🗺 Open Map
          </button>
          {onBrainDump && (
            <button onClick={() => { onBrainDump(); onClose(); }}
              className="w-full py-[7px] px-3 rounded-lg border-none text-[11px] font-bold cursor-pointer transition-all duration-150"
              style={{ background: "rgba(77,184,181,0.1)", color: "#4DB8B5" }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(77,184,181,0.18)"}
              onMouseLeave={e => e.currentTarget.style.background = "rgba(77,184,181,0.1)"}
            >
              🌍 Build Your World
            </button>
          )}
          <button disabled
            className="w-full py-[7px] px-3 rounded-lg border-[1.5px] border-dashed border-slate-200 bg-transparent text-[11px] font-semibold text-slate-400 cursor-not-allowed"
            title="Coming soon"
          >
            + Add Room or Building
          </button>
          <button onClick={handleResetWorld}
            className="w-full py-[7px] px-3 rounded-lg border-[1.5px] border-dashed bg-transparent text-[11px] font-semibold text-red-500 cursor-pointer transition-all duration-150"
            style={{ borderColor: "#FCA5A5" }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(239,68,68,0.05)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
          >
            ↺ Reset World
          </button>
        </div>
      </div>

      {fullMap && <FullMapOverlay currentRoom={currentRoom} onClose={() => setFullMap(false)} onNavigate={handleNavigate} worldData={activeWorldData} />}
    </>
  );
}
